import './App.css';
import React, { useState, useEffect, useCallback } from 'react';
import S3Selector from '../S3Selector/S3Selector';
import NodeInjector from '../../services/Dom/NodeInjector';
import EnvironmentName from '../EnvironmentName/EnvironmentName';
import ContextualBranchManager from '../../services/Dom/ContextualBranchManager.ts';
import Encoding from '../../services/Typing/Encoding.ts';

const DEFAULT_SCRIPT_ID = "scolab-switcher";

export interface IConfiguration {
  id: string;
  environmentName: string;
  s3: Array<{
    bucket: string;
    key: string;
    defaultSelection: string;
    onArtifactSelect: (branch: string, version: string) => void;
    subfolder?: string;
    skipDetails?: boolean;
  }>;
}

interface IProps {
  readonly configuration: IConfiguration;
  readonly unmount: () => void;
}

const getContextRootUrl = () => {
  const script = getScriptTag();
  const filename = getScriptName();
  const match = script.src.match(new RegExp(`/${filename}\\.js`));

  if (match) {
    return script.src.substring(0, match.index);
  }

  throw new Error(
      `Could not determine context root from script src: "${script.src}". ` +
      `The src attribute did not match known script patterns (e.g., /main.js, /qa.js, /publisher.js).`
  );
};

const getScriptName = () => {
  const script = getScriptTag();
  const match = script.src.match(/\/(\w+)\.js/);
  return match ? match[1] : '';
}

const getScriptTag = () => {
  const script = document.getElementById(DEFAULT_SCRIPT_ID) as HTMLScriptElement;
  if (!script) {
    throw new Error(`Please use the id '${DEFAULT_SCRIPT_ID}' on the script tag including the env-switcher.`);
  }

  return script;
}

const App: React.FC<IProps> = ({ configuration, unmount }) => {
  const [openedDialog, setOpenedDialog] = useState<string | null>(null);
  const [stylesLoaded, setStylesLoaded] = useState(false);

  useEffect(() => {
    const loadStyles = async () => {
      await NodeInjector.style(`${getContextRootUrl()}/${getScriptName()}.css`);
      setStylesLoaded(true);
    };
    loadStyles();
  }, []);

  const onDetails = useCallback((dialogKey: string) => {
    setOpenedDialog(prevDialog => prevDialog === dialogKey ? null : dialogKey);
  }, []);

  const getEnvironmentName = () => {
    if ("environmentName" in configuration) {
      return (
          <EnvironmentName
              value={configuration["environmentName"]}
          />
      );
    }
    return null;
  };

  const getS3Selector = () => {
    if ("s3" in configuration) {
      return Array.from(configuration["s3"]).map(config => {
        const uniqueKey = `scolab-s3selector-${config["key"]}`;
        return (<S3Selector
            key={uniqueKey}
            label={config["key"]}
            bucket={config["bucket"]}
            onDetails={() => onDetails(uniqueKey)}
            dialogIsOpened={openedDialog === uniqueKey}
            subfolder={config["subfolder"]}
            defaultSelection={config["defaultSelection"]}
            useDetailedView={config["skipDetails"] !== true}
            onArtifactSelect={config["onArtifactSelect"]}
        />);
      });
    }
    return null;
  };

  const copyShareUrl = useCallback(() => {
    const url = new URL(window.location.href);
    Array.from(configuration["s3"]).forEach(config => {
      const { key, defaultSelection } = config;
      const branch = ContextualBranchManager.getSelectedBranch(key) || defaultSelection;
      url.searchParams.set(`${key}`, Encoding.decode(branch));

      const version = ContextualBranchManager.getSelectedVersion(key) || 'latest';
      url.searchParams.set(`${key}Version`, version);
    });

    navigator.clipboard.writeText(url.toString());
  }, [configuration]);

  const getShareButton = () => {
    return (
        <button type="button" className="scolab-switcher-button" tabIndex={-1} onClick={copyShareUrl}
                title={"Share this selection"}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path
                d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/>
            <path
                d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z"/>
          </svg>
        </button>
    );
  };

  if (!stylesLoaded) {
    return null;
  }

  return (
      <div data-group="scolab-switcher" id="scolab-switcher-app">
        {getEnvironmentName()}
        {getS3Selector()}
        {getShareButton()}
        <button type="button" className="scolab-switcher-button" tabIndex={-1} onClick={unmount}>X</button>
      </div>
  );
};

export default App;
